import { useMemo, useState } from 'react'
import './DataGridComponent.scss'
import {
  DataGridBody,
  DataGridRow,
  DataGrid,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridCell,
  TableColumnDefinition,
  SearchBox,
  Button,
  Badge,
} from '@fluentui/react-components'
import {
  MessagesApplicationsUser,
  MessagesSubscriptionsOrganisationTeamUser,
  MessagesSupportImageItem,
} from '@/dotnet-client'
import { useFooterHeight } from '@/hooks'
import {
  MessagesApplicationsApplication,
  MessagesApplicationsApplicationTeam,
  MessagesStorageStorageDetailItem,
  MessagesStorageStorageDetailItemWorkspace,
  MessagesProjectsOrganisationProject,
  MessagesSubscriptionsOrganisationSummary,
  MessagesSubscriptionsOrganisationUser,
  MessagesSubscriptionsOrganisationWorkspace,
  MessagesSubscriptionsOrganisationWorkspaceSummary,
  MessagesWorkspacesEntity,
  MessagesApplicationsRoleTemplateAssetPermissions,
  MessagesLandingMenuItem,
} from '@/dotnet-client'
import { ApplicationRoleItem } from '../ApplicationControl/AppRolesPanel'
import { MessagesSubscriptionsOrganisationTeam } from '@/dotnet-client'

type DataGridType =
  | MessagesSubscriptionsOrganisationSummary
  | MessagesSupportImageItem
  | MessagesApplicationsApplication
  | ApplicationRoleItem
  | MessagesSubscriptionsOrganisationUser
  | MessagesProjectsOrganisationProject
  | MessagesSubscriptionsOrganisationTeam
  | MessagesSubscriptionsOrganisationWorkspace
  | MessagesSubscriptionsOrganisationWorkspaceSummary
  | MessagesWorkspacesEntity
  | MessagesApplicationsApplicationTeam
  | MessagesStorageStorageDetailItem
  | MessagesStorageStorageDetailItemWorkspace
  | MessagesApplicationsRoleTemplateAssetPermissions

type DataGridColumnDefinitions =
  | TableColumnDefinition<MessagesSubscriptionsOrganisationSummary>[]
  | TableColumnDefinition<MessagesSupportImageItem>[]
  | TableColumnDefinition<MessagesApplicationsApplication>[]
  | TableColumnDefinition<MessagesSubscriptionsOrganisationUser>[]
  | TableColumnDefinition<ApplicationRoleItem>[]
  | TableColumnDefinition<MessagesProjectsOrganisationProject>[]
  | TableColumnDefinition<MessagesSubscriptionsOrganisationTeam>[]
  | TableColumnDefinition<MessagesSubscriptionsOrganisationWorkspace>[]
  | TableColumnDefinition<MessagesSubscriptionsOrganisationWorkspaceSummary>[]
  | TableColumnDefinition<MessagesWorkspacesEntity>[]
  | TableColumnDefinition<MessagesApplicationsApplicationTeam>[]
  | TableColumnDefinition<MessagesStorageStorageDetailItem>[]
  | TableColumnDefinition<MessagesStorageStorageDetailItemWorkspace>[]
  | TableColumnDefinition<MessagesApplicationsRoleTemplateAssetPermissions>[]
  | TableColumnDefinition<MessagesLandingMenuItem & { level: number }>[]

type DataGridProps = {
  items?: unknown[] // ✅ Make `items` optional to handle undefined cases
  columns: DataGridColumnDefinitions
  onAdd?: () => void
  heightAdjustment?: number
}

export const DataGridComponent = ({ items = [], columns, onAdd, heightAdjustment }: DataGridProps) => {
  const [search, setSearch] = useState<string>('')
  const footerHeight = useFooterHeight()

  // ✅ Ensure `items` is always an array before calling `.filter()`
  const filteredItems = useMemo(() => {
    return items.filter((item) => JSON.stringify(item).toLowerCase().includes(search.toLowerCase()))
  }, [search, items])

  const getRowId = (item: DataGridType): string | number => {
    if (Object.prototype.hasOwnProperty.call(item, 'permissions')) {
      const roleItem = item as MessagesApplicationsRoleTemplateAssetPermissions
      return `${roleItem.id}_${roleItem.roleId}_${roleItem.resource}`
    }
    if (Object.prototype.hasOwnProperty.call(item, 'id')) {
      return (item as MessagesSubscriptionsOrganisationSummary).id ?? 'default-id'
    }
    if (Object.prototype.hasOwnProperty.call(item, 'blobName')) {
      return (item as MessagesStorageStorageDetailItem).blobName ?? 'default-blob'
    }
    if (Object.prototype.hasOwnProperty.call(item, 'workspaceId')) {
      return (item as MessagesSubscriptionsOrganisationWorkspace).workspaceId ?? 'default-workspace'
    }
    if (Object.prototype.hasOwnProperty.call(item, 'projectId')) {
      return (item as MessagesProjectsOrganisationProject).projectId ?? 'default-project'
    }
    if (Object.prototype.hasOwnProperty.call(item, 'entityId')) {
      return (item as MessagesWorkspacesEntity).entityId ?? 'default-entity'
    }
    if (Object.prototype.hasOwnProperty.call(item, 'filename')) {
      return (item as MessagesSupportImageItem).filename ?? 'default-filename'
    }
    if (Object.prototype.hasOwnProperty.call(item, 'applicationId')) {
      return (item as MessagesApplicationsApplication).applicationId ?? 'default-application'
    }
    if (Object.prototype.hasOwnProperty.call(item, 'applicationId')) {
      return (item as MessagesApplicationsApplicationTeam).applicationId ?? 'default-application'
    }
    if (Object.prototype.hasOwnProperty.call(item, 'id')) {
      return (item as MessagesApplicationsUser).id ?? 'default-id'
    }
    if (Object.prototype.hasOwnProperty.call(item, 'organisationId')) {
      return (item as MessagesSubscriptionsOrganisationTeam).organisationId ?? 'default-organisation'
    }
    if (Object.prototype.hasOwnProperty.call(item, 'applicationId')) {
      return (item as MessagesSubscriptionsOrganisationTeamUser).userId ?? 'default-user'
    }

    // Fallback to a unique default value
    return `default-${Math.random()}`
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
          marginBottom: '20px',
        }}
      >
        <SearchBox value={search} onChange={(_e, data) => setSearch(data.value || '')} />
      </div>

      <div
        style={{
          backgroundColor: 'white',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          padding: '20px',
        }}
      >
        <div>
          <Badge appearance="outline">{filteredItems.length} items</Badge>
        </div>
        {onAdd && <Button onClick={() => onAdd()}>Add item</Button>}
      </div>

      <DataGrid
        items={filteredItems}
        columns={columns}
        sortable
        getRowId={getRowId}
        style={{ backgroundColor: 'white' }}
      >
        <DataGridHeader>
          <DataGridRow>
            {({ renderHeaderCell }) => (
              <DataGridHeaderCell style={{ fontWeight: 'bold' }}>{renderHeaderCell()}</DataGridHeaderCell>
            )}
          </DataGridRow>
        </DataGridHeader>
        <DataGridBody<DataGridType>
          style={{
            maxHeight: `calc(100vh - ${footerHeight}px - 250px - ${heightAdjustment ?? 0}px)`,
            overflowY: 'auto',
          }}
        >
          {({ item, rowId }) => (
            <DataGridRow<DataGridType> key={rowId}>
              {({ renderCell }) => <DataGridCell>{renderCell(item)}</DataGridCell>}
            </DataGridRow>
          )}
        </DataGridBody>
      </DataGrid>
    </div>
  )
}

export default DataGridComponent
